/* eslint-disable @nx/enforce-module-boundaries */
import { toast } from '@intigral/shared';
import { log } from '@intigral/utils';
import h from '../../helpers/common';
import user from '../../helpers/user';
import { __ } from '../../translations';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IEntitlementService } from '@intigral/domain/lib/application/services/EntitlementService';
import { IPopcornService } from '@intigral/domain/lib/application/services/PopcornService';
import { getQitafProperty, resetQitafProperty } from './qitafServiceWrapper';
import { ISubscriptionService } from '@intigral/domain/lib/application/services/SubscriptionService';

const refreshSubscriptionAfterPurchase = async (handlePurchase?: () => void) => {
    const { authToken, username, identityToken } = user.getUserInfo() || {};
    const subscriptionService = container.resolve<ISubscriptionService>(DI.SERVICES.ISubscriptionService);
    const subscription = await subscriptionService.getSubscriptionStatus(
        user.getAPIKey(),
        username,
        authToken,
        identityToken
    );
    if (subscription) {
        user.updateUserSubscriptionsObject(subscription, false);
        handlePurchase?.();
    }
};

export const getTvodPricingTemplatesApi = async () => {
    const userInfo = user.getUserInfo();
    const operator = userInfo?.operator || 'stc';
    const entitlementService = container.resolve<IEntitlementService>(DI.SERVICES.IEntitlementService);
    return entitlementService.getTvodPricingTemplates(operator).then((response) => {
        const keys = Object.keys(response.data);
        const data = {};
        keys.forEach((key) => {
            data[key] =
                response.data[key][operator] ||
                response.data[key][operator.toLowerCase()] ||
                response.data[key][operator.toUpperCase()];
        });
        return data;
    });
};

export const tvodOneTimePurchaseApi = async (
    {
        productId,
        pin,
        password,
        currency = 'SAR',
    }: {
        productId: string;
        pin?: string;
        password?: string;
        currency?: string;
    },
    handlePurchase
) => {
    const username = user.getUsername();
    const purchaseData = {
        promotionCode: '',
        locale: h.getLang(),
        itemsToPurchase: [
            {
                productId,
                currency,
            },
        ],
        paymentRefDetails: {
            type: 'telco_billing',
        },
        qitafMsisdn: null,
        qitafRefId: null,
    };

    if (pin) {
        purchaseData['pin'] = pin;
    } else if (password) {
        purchaseData['password'] = password;
    }

    const qitafRefId = getQitafProperty('qitafRefId');
    if (qitafRefId) {
        purchaseData.qitafRefId = qitafRefId;
    }

    const qitafMsisdn = getQitafProperty('qitafMsisdn');
    if (qitafMsisdn) {
        purchaseData.qitafMsisdn = qitafMsisdn;
    }

    const entitlementService = container.resolve<IEntitlementService>(DI.SERVICES.IEntitlementService);

    return entitlementService
        .tVodPurchase(username, purchaseData, user.getUserToken(), user.getIdentityToken())
        .then(async (response) => {
            resetQitafProperty('qitafPoints');
            resetQitafProperty('qitafRefId');
            resetQitafProperty('qitafMsisdn');
            resetQitafProperty('upadtedPrice');
            await refreshSubscriptionAfterPurchase(handlePurchase);
            return response.data;
        })
        .catch((error) => {
            toast(__(`err_msg_${error?.data?.code}`), 'error');
        });
};

export const tvodGenerateOTPApi = async ({ productId }) => {
    const username = user.getUsername();
    const purchaseData = {
        promotionCode: '',
        locale: h.getLang(),
        itemsToPurchase: [
            {
                productId,
            },
        ],
        paymentRefDetails: {
            type: 'telco_billing',
        },
        qitafMsisdn: null,
        qitafRefId: null,
    };

    const qitafRefId = getQitafProperty('qitafRefId');
    if (qitafRefId) {
        purchaseData.qitafRefId = qitafRefId;
    }

    const qitafMsisdn = getQitafProperty('qitafMsisdn');
    if (qitafMsisdn) {
        purchaseData.qitafMsisdn = qitafMsisdn;
    }
    const entitlementService = container.resolve<IEntitlementService>(DI.SERVICES.IEntitlementService);
    return entitlementService.tvodOtpGenerate(purchaseData, username, user.getUserToken(), user.getIdentityToken());
};

export const tvodForgetPasswordApi = async () => {
    const username = user.getUsername();
    const popcornService = container.resolve<IPopcornService>(DI.SERVICES.IPopcornService);

    return popcornService
        .forgotPin(user.getAPIKey(), username, user.getUserToken(), user.getIdentityToken())
        .then((response) => {
            return response;
        })
        .catch((error) => {
            toast(__(`err_msg_${error?.data?.code}`), 'error');
        });
};

export const tvodVerifyOTPApi = async ({ otp, productId }, handlePurchase) => {
    const username = user.getUsername();
    const purchaseData = {
        promotionCode: '',
        locale: h.getLang(),
        otp: otp,
        itemsToPurchase: [
            {
                productId,
            },
        ],
        paymentRefDetails: {
            type: 'telco_billing',
        },
        qitafMsisdn: null,
        qitafRefId: null,
    };

    const qitafRefId = getQitafProperty('qitafRefId');
    if (qitafRefId) {
        purchaseData.qitafRefId = qitafRefId;
    }

    const qitafMsisdn = getQitafProperty('qitafMsisdn');
    if (qitafMsisdn) {
        purchaseData.qitafMsisdn = qitafMsisdn;
    }
    const entitlementService = container.resolve<IEntitlementService>(DI.SERVICES.IEntitlementService);
    return entitlementService
        .tvodOtpVerify(purchaseData, username, user.getUserToken(), user.getIdentityToken())
        .then(async (response) => {
            resetQitafProperty('qitafPoints');
            resetQitafProperty('qitafRefId');
            resetQitafProperty('qitafMsisdn');
            resetQitafProperty('upadtedPrice');
            await refreshSubscriptionAfterPurchase(handlePurchase);
            return response;
        })
        .catch((error) => {
            toast(__(`err_msg_${error?.data?.code}`, { code: error?.data?.code }), 'error');
        });
};
